import { z } from 'zod';
/**
 * Contrats de la CONFIGURATION LLM (T5 lot A, XPHOTO-26) — source unique front + back.
 *
 * Fondation du tagging IA : configurer un fournisseur de vision (LM Studio local,
 * Claude, Gemini, Grok), TESTER la connexion, DÉCOUVRIR et choisir un modèle, et
 * persister le « mode d'action IA ». Le job de tagging, la taxonomie et l'éditeur
 * de validation viennent dans des lots séparés (B/C/D) — hors périmètre ici.
 *
 * ⚠ SÉCURITÉ (ADR-0002 §E) : la clé d'API n'est JAMAIS stockée ni renvoyée. On ne
 * porte que le NOM de la variable d'environnement (`apiKeyEnv`) ; le backend lit
 * `process.env[apiKeyEnv]` au moment du test/de la découverte, sans jamais
 * l'exposer. Le test rapporte « clé présente » (booléen), jamais la clé.
 */
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
/**
 * Nature d'un fournisseur LLM (aligné sur l'enum PostgreSQL `ai_provider_kind`).
 * `anthropic` (Claude) est ajouté par la migration 0015 ; `openai_compat` sert
 * LM Studio (serveur local compatible OpenAI) ; `grok` (xAI) est aussi compatible
 * OpenAI. `ollama` reste supporté (déjà dans l'enum d'origine).
 */
export const aiProviderKindSchema = z.enum([
    'openai_compat',
    'ollama',
    'anthropic',
    'gemini',
    'grok',
]);
/**
 * Mode d'action IA — comment l'IA déclenche ses actions (persisté ici ; consommé
 * par le futur lot de tagging).
 * - `auto`         : tente les outils natifs puis retombe sur le JSON en cas d'échec.
 * - `native_tools` : exige le function-calling (modèle capable).
 * - `json_in_text` : JSON dans le texte (modèles sans function-calling, ex. Gemma) —
 *   évite un appel voué à l'échec.
 */
export const aiActionModeSchema = z.enum(['auto', 'native_tools', 'json_in_text']);
// ---------------------------------------------------------------------------
// Bornes de saisie
// ---------------------------------------------------------------------------
export const AI_PROVIDER_NAME_MAX = 120;
export const AI_BASE_URL_MAX = 512;
export const AI_MODEL_MAX = 200;
export const AI_API_KEY_ENV_MAX = 120;
/** Bornes du plancher de capture (fraction 0..1) : on ne persiste que les tags ≥ plancher. */
export const AI_CAPTURE_FLOOR_MIN = 0;
export const AI_CAPTURE_FLOOR_MAX = 1;
/** Défaut du plancher de capture (10 %) — modéré : capte les tags un peu incertains, borne le bruit. */
export const AI_CAPTURE_FLOOR_DEFAULT = 0.1;
/** Borne (généreuse) de la taille d'un template de prompt d'indexation personnalisé. */
export const AI_TAG_PROMPT_MAX = 8000;
/**
 * PRÉFIXE OBLIGATOIRE des variables d'environnement portant une clé de fournisseur LLM.
 *
 * ⚠ SÉCURITÉ (correctif d'audit) : `apiKeyEnv` désigne une variable que le serveur LIT
 * dans `process.env` puis ÉMET en en-tête `Authorization: Bearer …` vers une `baseUrl`
 * choisie par l'appelant. Un simple filtre de FORME POSIX (`^[A-Za-z_][A-Za-z0-9_]*$`)
 * ne protégeait de rien : `SESSION_SECRET`, `DATABASE_URL` et `SUPERADMIN_PASSWORD` le
 * satisfaisaient tous → un appelant pouvait faire EXFILTRER n'importe quel secret du
 * processus vers un serveur tiers. On impose donc un ESPACE DE NOMS dédié : seule une
 * variable préfixée peut être résolue, et ce préfixe n'est jamais porté par un secret
 * d'infrastructure.
 */
export const AI_API_KEY_ENV_PREFIX = 'XPHOTO_AI_KEY_';
/**
 * Motif complet d'un `apiKeyEnv` acceptable : le préfixe dédié suivi d'au moins un
 * caractère d'identifiant POSIX (ex. `XPHOTO_AI_KEY_ANTHROPIC`). Exporté pour que le
 * SERVEUR applique la même règle au point de résolution (`AiService`) — la validation
 * de contrat seule ne protégerait pas les lignes déjà en base.
 */
export const AI_API_KEY_ENV_PATTERN = /^XPHOTO_AI_KEY_[A-Za-z0-9_]+$/;
/**
 * Le nom de variable est-il dans l'allow-list ? PUR. La chaîne vide est ACCEPTÉE :
 * elle signifie « aucune clé requise » (cas LM Studio local) et ne résout rien.
 */
export function isAllowedApiKeyEnv(name) {
    return name === '' || AI_API_KEY_ENV_PATTERN.test(name);
}
/**
 * Nom de variable d'environnement (`apiKeyEnv`) : allow-list stricte (préfixe dédié
 * `XPHOTO_AI_KEY_`, cf. ci-dessus). ⚠ PAS de `.transform` (boot-crash `z.toJSONSchema`
 * quand le schéma sert de corps/query de route) — un `.regex()` reste représentable
 * en JSON Schema (`pattern`).
 *
 * PORTÉE : ce schéma garde l'ÉCRITURE (création/mise à jour d'un fournisseur) et les
 * SONDES. La LECTURE (`aiProviderSchema.apiKeyEnv`) reste un `z.string()` tolérant :
 * une ligne déjà en base portant un ancien nom hors allow-list doit rester LISIBLE
 * (l'écran « Configuration LLM » doit pouvoir l'afficher pour qu'on la corrige) — mais
 * le serveur refusera de RÉSOUDRE cette variable, cf. `AiService.resolveKey`.
 */
export const aiApiKeyEnvSchema = z
    .string()
    .max(AI_API_KEY_ENV_MAX)
    .regex(AI_API_KEY_ENV_PATTERN, `Le nom de la variable d'environnement doit commencer par ${AI_API_KEY_ENV_PREFIX}`);
// ---------------------------------------------------------------------------
// DTO fournisseur (jamais la clé, seulement le NOM de sa variable d'env)
// ---------------------------------------------------------------------------
/**
 * Fournisseur LLM tel qu'exposé par l'API (`GET`/`POST`/`PATCH`). `apiKeyEnv` est
 * le NOM de la variable d'env portant la clé — jamais la clé elle-même. `libraryId`
 * `null` = fournisseur global (partagé). `apiKeyPresent` reflète la présence de la
 * variable d'env côté serveur (`process.env[apiKeyEnv]` non vide) SANS divulguer sa
 * valeur — sert au badge « Clé API détectée ».
 */
export const aiProviderSchema = z.object({
    id: z.uuid(),
    /** `null` = fournisseur global (non rattaché à une bibliothèque). */
    libraryId: z.uuid().nullable(),
    name: z.string(),
    kind: aiProviderKindSchema,
    baseUrl: z.string(),
    /** NOM de la variable d'env portant la clé (jamais la clé). */
    apiKeyEnv: z.string(),
    model: z.string(),
    actionMode: aiActionModeSchema,
    /** Plancher de capture (fraction 0..1) : on ne persiste que les tags de score ≥ plancher. */
    captureFloor: z.number().min(AI_CAPTURE_FLOOR_MIN).max(AI_CAPTURE_FLOOR_MAX),
    /** Template de prompt d'indexation (EN ANGLAIS) ; `null` = template par défaut intégré. */
    promptTemplate: z.string().nullable(),
    isDefault: z.boolean(),
    enabled: z.boolean(),
    /** La variable d'env `apiKeyEnv` est-elle présente/non vide côté serveur ? */
    apiKeyPresent: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
});
/** Liste des fournisseurs configurés (`GET /api/libraries/:id/ai-providers`). */
export const aiProviderListSchema = z.object({
    providers: z.array(aiProviderSchema),
});
// ---------------------------------------------------------------------------
// Création / mise à jour
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/ai-providers`. `apiKeyEnv` est optionnel (LM
 * Studio local n'exige aucune clé → chaîne vide/omission). `isDefault` : si vrai, le
 * fournisseur devient l'unique défaut de sa portée (`enabled` implicitement vrai à
 * la création). La clé N'EST JAMAIS transmise ici — seul le nom de sa variable d'env.
 */
export const createAiProviderRequestSchema = z.object({
    name: z.string().min(1).max(AI_PROVIDER_NAME_MAX),
    kind: aiProviderKindSchema,
    baseUrl: z.string().min(1).max(AI_BASE_URL_MAX),
    /** NOM de la variable d'env (vide = aucune clé requise, cas LM Studio). */
    apiKeyEnv: aiApiKeyEnvSchema.or(z.literal('')).default(''),
    model: z.string().min(1).max(AI_MODEL_MAX),
    actionMode: aiActionModeSchema.default('auto'),
    /** Plancher de capture (fraction 0..1, défaut 10 %). */
    captureFloor: z
        .number()
        .min(AI_CAPTURE_FLOOR_MIN)
        .max(AI_CAPTURE_FLOOR_MAX)
        .default(AI_CAPTURE_FLOOR_DEFAULT),
    /**
     * Template de prompt d'indexation (EN ANGLAIS). Optionnel/nullable — `null`/omis = template
     * par défaut. ⚠ PAS de `.transform` (boot-crash Zod v4) : la normalisation « chaîne
     * vide/blanche → null » se fait dans le HANDLER de route.
     */
    promptTemplate: z.string().max(AI_TAG_PROMPT_MAX).nullable().optional(),
    isDefault: z.boolean().default(false),
    enabled: z.boolean().default(true),
});
/**
 * Corps de `PATCH /api/libraries/:id/ai-providers/:providerId`. Tous les champs
 * optionnels ; au moins un requis (`refine`). Sert aussi à « activer » un
 * fournisseur (`isDefault: true` → il devient l'unique défaut de la portée).
 */
export const updateAiProviderRequestSchema = z
    .object({
    name: z.string().min(1).max(AI_PROVIDER_NAME_MAX).optional(),
    kind: aiProviderKindSchema.optional(),
    baseUrl: z.string().min(1).max(AI_BASE_URL_MAX).optional(),
    apiKeyEnv: aiApiKeyEnvSchema.or(z.literal('')).optional(),
    model: z.string().min(1).max(AI_MODEL_MAX).optional(),
    actionMode: aiActionModeSchema.optional(),
    captureFloor: z.number().min(AI_CAPTURE_FLOOR_MIN).max(AI_CAPTURE_FLOOR_MAX).optional(),
    /** ⚠ PAS de `.transform` : « vide → null » se fait dans le HANDLER de route. */
    promptTemplate: z.string().max(AI_TAG_PROMPT_MAX).nullable().optional(),
    isDefault: z.boolean().optional(),
    enabled: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Au moins un champ à mettre à jour',
});
// ---------------------------------------------------------------------------
// Découverte de modèles & test de connexion (santé)
// ---------------------------------------------------------------------------
/**
 * Un modèle découvert auprès du fournisseur (`id` = identifiant à charger, `label`
 * = affichage éventuel, sinon l'`id`). Volontairement minimal : l'écran peuple un
 * `<select>` à partir de la liste.
 */
export const aiModelSchema = z.object({
    id: z.string(),
    label: z.string().nullable(),
});
/**
 * Résultat de `POST …/ai-providers/discover-models` (ou `GET …/models`). `ok` =
 * la découverte a abouti ; `models` = liste (vide si `ok` mais aucun modèle) ;
 * `apiKeyPresent` = la variable d'env était présente ; `error` = message court
 * DIAGNOSTIC (jamais la clé) en cas d'échec. Ne fait JAMAIS échouer la route HTTP
 * (un fournisseur injoignable renvoie `ok:false`, pas un 500).
 */
export const aiModelsResultSchema = z.object({
    ok: z.boolean(),
    models: z.array(aiModelSchema),
    apiKeyPresent: z.boolean(),
    error: z.string().nullable(),
});
/**
 * Résultat de `POST …/ai-providers/test-connection`. `reachable` = le fournisseur a
 * répondu ; `apiKeyPresent` = la variable d'env était présente ; `modelCount` = nb
 * de modèles découverts (indice de bonne santé) ; `error` = message court
 * DIAGNOSTIC (jamais la clé) en cas d'échec. Ne fait JAMAIS échoyer la clé.
 */
export const aiTestConnectionResultSchema = z.object({
    reachable: z.boolean(),
    apiKeyPresent: z.boolean(),
    modelCount: z.number().int().nonnegative(),
    error: z.string().nullable(),
});
/**
 * Corps commun de test/découverte à partir d'un fournisseur NON encore enregistré
 * (l'écran teste AVANT d'enregistrer). Mêmes champs que la création, mais sans
 * `name`/`isDefault`/`enabled` (non nécessaires au test). `apiKeyEnv` optionnel.
 */
export const aiProbeRequestSchema = z.object({
    kind: aiProviderKindSchema,
    baseUrl: z.string().min(1).max(AI_BASE_URL_MAX),
    apiKeyEnv: aiApiKeyEnvSchema.or(z.literal('')).default(''),
    /** Modèle optionnel : certains fournisseurs le tolèrent pour la découverte. */
    model: z.string().max(AI_MODEL_MAX).optional(),
});
/**
 * Corps de `POST …/ai-providers/test-vision` (DOUBLE TEST du modèle sélectionné) :
 * comme `aiProbeRequestSchema` mais le `model` est REQUIS (on teste un modèle précis).
 * PAS de `.transform` → représentable par `z.toJSONSchema` (garde boot).
 */
export const aiVisionTestRequestSchema = aiProbeRequestSchema.extend({
    model: z.string().min(1).max(AI_MODEL_MAX),
});
/**
 * Résultat du DOUBLE TEST de vision (`POST …/ai-providers/test-vision`) :
 *  - `reachable`      : le fournisseur a répondu à au moins un des deux appels.
 *  - `modelResponds`  : (A) le modèle a répondu à une invite TEXTE (il est chargé/génère).
 *  - `imageAccepted`  : (B) le modèle a accepté une MINI-IMAGE (il est multimodal/vision).
 * `*Error` = message DIAGNOSTIC du fournisseur (jamais la clé) en cas d'échec — p.ex.
 * « HTTP 400: … does not support image inputs » (le modèle est texte-seul). Ne fait
 * JAMAIS échouer la route HTTP (résultat structuré, jamais un 500).
 */
export const aiVisionTestResultSchema = z.object({
    reachable: z.boolean(),
    modelResponds: z.boolean(),
    modelError: z.string().nullable(),
    imageAccepted: z.boolean(),
    imageError: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Tagging IA (T5 lot C) — proposition de tags par le LLM de vision
// ---------------------------------------------------------------------------
/** Nombre de tags proposés par défaut par image (borné pour rester lisible). */
export const AI_TAG_MAX_DEFAULT = 12;
/** Borne haute absolue du nombre de tags demandés par image. */
export const AI_TAG_MAX_LIMIT = 30;
/**
 * Corps de `POST /api/libraries/:id/ai-tag` — enfile un job `ai_tag` qui envoie les
 * photos ciblées au LLM de vision configuré (fournisseur par défaut ACTIVÉ de la
 * bibliothèque) et STOCKE des tags PROPOSÉS pour validation humaine ultérieure (lot D).
 *
 * Portée exclusive :
 *  - `all`       : tous les médias PRÉSENTS de la bibliothèque.
 *  - `folder`    : le sous-arbre `folder` (préfixe de `rel_path`) — `folder` REQUIS.
 *  - `selection` : les `assetIds` fournis (non vide) — REQUIS.
 * `maxTags` borne le nombre de tags proposés par image (défaut 12, plafond 30).
 * `dryRun` : aperçu (aucune proposition écrite).
 */
export const aiTagRequestSchema = z
    .object({
    scope: z.enum(['all', 'folder', 'selection']),
    /** Sous-arbre ciblé (préfixe `rel_path`), requis quand `scope === 'folder'`. */
    folder: z.string().max(1024).optional(),
    /** IDs d'assets ciblés, requis (non vide) quand `scope === 'selection'`. */
    assetIds: z.array(z.uuid()).optional(),
    maxTags: z.coerce.number().int().min(1).max(AI_TAG_MAX_LIMIT).default(AI_TAG_MAX_DEFAULT),
    dryRun: z.boolean().default(false),
})
    .refine((v) => v.scope !== 'folder' || (typeof v.folder === 'string' && v.folder.length > 0), {
    message: 'La portée « dossier » exige un dossier',
    path: ['folder'],
})
    .refine((v) => v.scope !== 'selection' || (Array.isArray(v.assetIds) && v.assetIds.length > 0), {
    message: 'La portée « sélection » exige au moins un asset',
    path: ['assetIds'],
});
/**
 * Résultat d'un job `ai_tag` (persisté dans `jobs.result`). `assetsProcessed` =
 * médias tentés ; `assetsTagged` = médias ayant reçu ≥ 1 NOUVELLE proposition ;
 * `tagsProposed` = liaisons `proposed` NEUVES insérées (hors doublons/déjà décidés) ;
 * `assetsFailed` = médias sautés (fournisseur injoignable, réponse illisible, image
 * manquante) — comptés SANS jamais faire échouer le job entier.
 *
 * MONDE FERMÉ (Lot 2) : un label renvoyé HORS taxonomie est simplement IGNORÉ (jamais
 * stocké, jamais proposé à une revue) — d'où l'absence de compteur de « suggestions ».
 */
export const aiTagResultSchema = z.object({
    assetsProcessed: z.number().int().nonnegative(),
    assetsTagged: z.number().int().nonnegative(),
    tagsProposed: z.number().int().nonnegative(),
    assetsFailed: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Template de prompt d'indexation (source UNIQUE front + back)
// ---------------------------------------------------------------------------
/**
 * Template PAR DÉFAUT du prompt de tagging (EN ANGLAIS). Placeholders substitués par
 * `renderTagPrompt` :
 *   - `{{tags}}`    : la taxonomie formatée (`label (description)` séparés par des virgules) ;
 *   - `{{maxTags}}` : borne haute (garde-fou) du nombre de tags renvoyés.
 *
 * DÉCISION (retour utilisateur) : le modèle doit NOTER TOUS les tags de la liste (score 0..1
 * par tag) et NE JAMAIS en omettre — l'ancien « ne garde que les tags qui s'appliquent »
 * faisait taire silencieusement les tags incertains. Le PLANCHER DE CAPTURE (côté serveur)
 * borne ensuite ce qui est persisté ; le SEUIL de validation filtre ce qui est affiché.
 * Monde fermé : n'utiliser QUE les tags de la liste, jamais en inventer.
 */
export const DEFAULT_TAG_PROMPT_TEMPLATE = [
    `You are a photo indexing assistant. Look at the image and SCORE EVERY tag in the CLOSED LIST below.`,
    `ABSOLUTE RULE: use ONLY tags from this list, exactly as written. You must NEVER invent, translate, or rephrase a tag. Do NOT add any new tag.`,
    `For EVERY tag in the list, output one entry with a "confidence" between 0.0 and 1.0 = the probability that the tag applies to the image (0 = certainly absent, 1 = certainly present). NEVER omit a tag and NEVER drop a low score — return the COMPLETE list, one entry per tag.`,
    `Allowed list (a parenthesis, when present, clarifies when to apply the tag; do NOT write the parenthesis in your answer): {{tags}}.`,
    `Safety cap: never return more than {{maxTags}} tags with a clearly non-zero score, but always cover the whole list.`,
    `Respond ONLY with a valid JSON array, with no surrounding text, of the form:`,
    `[{"label": "cat", "confidence": 0.95}, {"label": "indoor", "confidence": 0.1}]`,
    `Each "label" is copied EXACTLY from the list; "confidence" is a number between 0 and 1.`,
    `Do not add any explanation, Markdown, or extra key.`,
].join('\n');
/**
 * Rend un template de prompt en substituant TOUTES les occurrences de `{{tags}}` et
 * `{{maxTags}}`. PUR (aucun I/O) — partagé front (aperçu/placeholder) et back (job). Un
 * template sans placeholder est renvoyé tel quel (rien à substituer).
 */
export function renderTagPrompt(template, vars) {
    return template
        .split('{{tags}}')
        .join(vars.taxonomy)
        .split('{{maxTags}}')
        .join(String(vars.maxTags));
}
//# sourceMappingURL=ai.js.map